import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Component, Inject, OnInit, OnDestroy, inject, Optional } from '@angular/core';
import { HatLocationPipe } from '../../shared/hat-location.pipe';
import {
    Organization,
    OrgPolicy,
    OrgHat,
    PolicyStatus,
    PolicyCategory,
    PolicyApprovalStep,
    PolicyPartTarget,
    PolicyHatTarget,
    PolicyUserTarget,
    PolicyTargetsSnapshot,
    PolicyApprovalWorkflowSnapshot,
    PolicyAcknowledgment,
} from '@newgenus/common';
import { MaterialModule } from '../../../material.module';
import { CommonModule } from '@angular/common';
import { AngularFirestore } from '@newgenus/fire-compat/firestore';
import { AngularFireAuth } from '@newgenus/fire-compat/auth';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Timestamp } from 'firebase/firestore';
import { take, Subject, takeUntil } from 'rxjs';
import { DialogService } from '../../../../services/dialog.service';
import { stampBlame } from '../../shared/policy-blame';
import { stampLastChangedBy } from '../../shared/last-changed-by';
import { PolicyApprovalService } from '../../services/policy-approval.service';
import { OrgDialogService } from '../../services/org-dialog.service';
import { ProfileColorService } from '../../services/profile-color.service';
import { OrgBoardService } from '../../services/org-board-service';

/**
 * Policy status display configuration
 */
export const POLICY_STATUS_CONFIG: Record<PolicyStatus, { label: string; icon: string; color: string }> = {
    draft: { label: 'Draft', icon: 'edit', color: '#9e9e9e' },
    pending_approval: { label: 'Pending Approval', icon: 'hourglass_empty', color: '#ff9800' },
    approved: { label: 'Approved', icon: 'check_circle', color: '#2196f3' },
    scheduled: { label: 'Scheduled', icon: 'schedule', color: '#2196f3' },
    published: { label: 'Published', icon: 'publish', color: '#4caf50' },
    expired: { label: 'Expired', icon: 'event_busy', color: '#9e9e9e' },
    cancelled: { label: 'Cancelled', icon: 'cancel', color: '#f44336' },
    revoked: { label: 'Revoked', icon: 'block', color: '#f44336' },
    archived: { label: 'Archived', icon: 'archive', color: '#795548' },
    deleted: { label: 'Deleted', icon: 'delete', color: '#f44336' },
};

/**
 * Policy category display configuration
 */
export const POLICY_CATEGORIES: { value: PolicyCategory; label: string; icon: string }[] = [
    { value: 'general', label: 'General', icon: 'description' },
    { value: 'hr', label: 'Human Resources', icon: 'people' },
    { value: 'finance', label: 'Finance', icon: 'account_balance' },
    { value: 'it', label: 'IT & Technology', icon: 'computer' },
    { value: 'legal', label: 'Legal', icon: 'gavel' },
    { value: 'compliance', label: 'Compliance', icon: 'verified_user' },
    { value: 'safety', label: 'Health & Safety', icon: 'health_and_safety' },
    { value: 'operations', label: 'Operations', icon: 'settings' },
    { value: 'custom', label: 'Custom', icon: 'category' },
];

export interface PolicyDetailsDialogData {
    policy: OrgPolicy;
    policyKey: string;
    organization: Organization;
}

interface HatUsingPolicy {
    key: string;
    name: string;
    userCount: number;
}

interface UserViaHat {
    key: string;
    name: string;
    hatNames: string[];
    /** Acknowledgment status: 'acknowledged', 'pending', 'not_required' */
    acknowledgmentStatus?: 'acknowledged' | 'pending' | 'not_required';
    /** When the user acknowledged (if applicable) */
    acknowledgedAt?: Date | null;
}

interface PositionWithPolicy {
    key: string;
    name: string;
    hatName: string;
    partType?: string;
}

interface RevisionChange {
    field: string;
    label: string;
    icon: string;
    fromValue?: string;
    toValue?: string;
}

interface RevisionDisplay {
    revisionNumber: number; // Sequential revision number (1, 2, 3...)
    version: number; // Original version from data
    date: Date | string | null;
    name: string;
    writtenBy: string;
    policyWriting?: string;
    changeNotes?: string;
    changes?: RevisionChange[];
    isInitial?: boolean;

    // Full snapshot data for display
    description?: string;
    category?: PolicyCategory;
    customCategory?: string;
    status?: PolicyStatus;
    effectiveDate?: Date | null;
    publishDate?: Date | null;
    expiryDate?: Date | null;
    acknowledgmentDeadline?: Date | null;
    targets?: PolicyTargetsSnapshot;
    approvalWorkflow?: PolicyApprovalWorkflowSnapshot;
    requiresAcknowledgment?: boolean;
    isMandatory?: boolean;
}

/**
 * Approval step status configuration for display
 */
export const APPROVAL_STATUS_CONFIG: Record<string, { label: string; icon: string; color: string }> = {
    pending: { label: 'Pending', icon: 'schedule', color: '#9e9e9e' },
    approved: { label: 'Approved', icon: 'check_circle', color: '#4caf50' },
    rejected: { label: 'Rejected', icon: 'cancel', color: '#f44336' },
    skipped: { label: 'Skipped', icon: 'skip_next', color: '#ff9800' },
};

@Component({
    selector: 'shared-org-policy-details-dialog',
    imports: [HatLocationPipe, MaterialModule, CommonModule],
    templateUrl: './org-policy-details-dialog.html',
    styleUrls: ['../org-dialog.scss', '../../organization-board.component.scss', './org-policy-details-dialog.scss'],
})
export class OrgPolicyDetailsDialogComponent implements OnInit, OnDestroy {
    public policy: OrgPolicy | null = null;
    public formattedPolicyContent = ''; // HTML-formatted policy content for display
    public hatsUsingPolicy: HatUsingPolicy[] = [];
    public usersViaHats: UserViaHat[] = [];
    public positionsWithPolicy: PositionWithPolicy[] = [];
    public revisions: RevisionDisplay[] = [];

    // Status & Category config
    public statusConfig = POLICY_STATUS_CONFIG;
    public categories = POLICY_CATEGORIES;
    public approvalStatusConfig = APPROVAL_STATUS_CONFIG;

    // Creator name cache
    private userNameCache = new Map<string, string>();
    private profileColorService = inject(ProfileColorService);
    public creatorName = '';

    // Live org updates
    private destroy$ = new Subject<void>();
    private obService = inject(OrgBoardService);

    // Section collapse states (true = expanded, false = collapsed)
    public sectionStates = {
        content: true,
        users: true,
        positions: true,
        hats: true,
        targeting: false, // Collapsed by default
        approval: true,
        adminOverride: true,
        auditTrail: false, // Collapsed by default
        revisions: true,
    };

    // Inject DialogService for modal confirmations
    private dialogService = inject(DialogService);

    // === Approval Chain State (OE-75) ===
    /** The signed-in user, resolved once — the per-step buttons gate on it. */
    public currentUid = '';
    /** True while an approval call is in flight (disables the step buttons). */
    public approvalInFlight = false;

    /** The only route to an approval transition — never write one directly. */
    private approvalService = inject(PolicyApprovalService);

    constructor(
        public dialogRef: MatDialogRef<OrgPolicyDetailsDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: PolicyDetailsDialogData,
        private afs: AngularFirestore,
        private auth: AngularFireAuth,
        private snackbar: MatSnackBar,
        private dialog: MatDialog,
        @Optional() public orgDialogService: OrgDialogService
    ) {
        // Full-bleed on phones (DESIGN.md §Sheets, OE-153 item 4) — the class
        // is phone-scoped globally, desktop unaffected.
        this.dialogRef.addPanelClass('ng-fullbleed-dialog');
    }

    public ngOnInit(): void {
        console.log('OrgPolicyDetailsDialogComponent > ngOnInit > data:', this.data);

        // Initialize with dialog data immediately
        this.policy = this.data.policy;
        this.formattedPolicyContent = this.convertToHtml(this.policy?.policyWriting || '');
        this.loadPolicyDetails();
        this.loadCreatorName();

        // Subscribe to live org updates from the org board doc cache
        this.subscribeToOrgUpdates();

        // Who is looking — decides whether this user sees Approve/Reject
        this.loadCurrentUid();
    }

    /** Resolve the signed-in uid for the per-step approval gating. */
    private async loadCurrentUid(): Promise<void> {
        const user = await this.auth.currentUser;
        this.currentUid = user?.uid ?? '';
    }

    /** Subscribe to live organization updates so the dialog refreshes when data changes */
    private subscribeToOrgUpdates(): void {
        const policyKey = this.data.policyKey;
        this.obService.organization$.pipe(takeUntil(this.destroy$)).subscribe((org) => {
            if (!org) return;
            this.data.organization = org;
            if (policyKey && org.orgPolicies?.[policyKey]) {
                const updatedPolicy = org.orgPolicies[policyKey] as OrgPolicy;
                const statusChanged = this.policy?.status !== updatedPolicy.status;
                const oldHatCount = this.policy?.targets?.resolvedHatKeys?.length || 0;
                const newHatCount = updatedPolicy?.targets?.resolvedHatKeys?.length || 0;
                const hatCountChanged = oldHatCount !== newHatCount;

                this.policy = updatedPolicy;
                this.data.policy = updatedPolicy;
                this.formattedPolicyContent = this.convertToHtml(updatedPolicy.policyWriting || '');
                this.loadRevisions();
                if (statusChanged || hatCountChanged) {
                    this.loadPolicyDetails();
                }
            }
        });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Open the policy edit dialog with this policy pre-loaded for editing.
     * Closes the details dialog and opens the edit dialog.
     */
    public openEditDialog(): void {
        if (!this.policy) {
            this.snackbar.open('No policy to edit', 'Close', { duration: 3000 });
            return;
        }

        // Close this details dialog
        this.dialogRef.close();

        // Open the policy dialog in edit mode
        import('../org-policy-dialog/org-policy-dialog').then((module) => {
            this.dialog.open(module.OrgPolicyDialogComponent, {
                width: '90vw',
                maxWidth: '1400px',
                height: '90vh',
                data: {
                    organization: this.data.organization,
                    goToNewTab: false,
                    policyToEdit: this.policy,
                    apiBaseUrl: this.orgDialogService?.apiBaseUrl, // Enable backend API for publish
                },
                panelClass: 'policy-dialog',
            });
        });
    }

    /**
     * Load the creator's display name from Firestore
     */
    private loadCreatorName(): void {
        const createdBy = this.policy?.createdBy;
        if (!createdBy) {
            this.creatorName = '';
            return;
        }

        // Check cache first
        if (this.userNameCache.has(createdBy)) {
            this.creatorName = this.userNameCache.get(createdBy) || '';
            return;
        }

        // Set loading state immediately to prevent repeated calls
        this.userNameCache.set(createdBy, 'Loading...');
        this.creatorName = 'Loading...';

        // Try organization users first
        const orgKey = this.data.organization?.key;
        if (orgKey) {
            this.afs
                .collection(`organizations/${orgKey}/users`)
                .doc(createdBy)
                .get()
                .pipe(take(1))
                .subscribe({
                    next: (doc) => {
                        if (doc.exists) {
                            const userData = doc.data() as { name?: string; displayName?: string; email?: string };
                            const name = userData?.name || userData?.displayName || userData?.email || createdBy;
                            this.userNameCache.set(createdBy, name);
                            this.creatorName = name;
                        } else {
                            // Try global users collection
                            this.loadFromGlobalUsers(createdBy);
                        }
                    },
                    error: () => this.loadFromGlobalUsers(createdBy),
                });
        } else {
            this.loadFromGlobalUsers(createdBy);
        }
    }

    private loadFromGlobalUsers(uid: string): void {
        this.afs
            .collection('users')
            .doc(uid)
            .get()
            .pipe(take(1))
            .subscribe({
                next: (doc) => {
                    if (doc.exists) {
                        const userData = doc.data() as { name?: string; displayName?: string; email?: string };
                        const name = userData?.name || userData?.displayName || userData?.email || uid;
                        this.userNameCache.set(uid, name);
                        this.creatorName = name;
                    } else {
                        this.userNameCache.set(uid, uid);
                        this.creatorName = uid;
                    }
                },
                error: () => {
                    this.userNameCache.set(uid, uid);
                    this.creatorName = uid;
                },
            });
    }

    private loadPolicyDetails(): void {
        if (!this.policy) {
            console.warn('No policy data provided');
            return;
        }

        const org = this.data.organization;

        // Only load users, hats, and positions when policy is published
        // Other statuses (draft, deleted, cancelled, etc.) should not show active assignments
        if (this.policy.status === 'published') {
            // Load hats using this policy
            this.loadHatsUsingPolicy(org);

            // Load users via hats
            this.loadUsersViaHats(org);

            // Load positions via hats
            this.loadPositionsWithPolicy(org);
        } else {
            // Clear any stale data for non-published policies
            this.hatsUsingPolicy = [];
            this.usersViaHats = [];
            this.positionsWithPolicy = [];
        }

        // Load revisions (always show regardless of status)
        this.loadRevisions();
    }

    private loadHatsUsingPolicy(org: Organization): void {
        const policyKey = this.data.policyKey;
        const orgHats = org?.orgHats;

        if (!orgHats?.hatsKeys) {
            this.hatsUsingPolicy = [];
            return;
        }

        this.hatsUsingPolicy = [];

        // Query model: Check resolvedHatKeys on the policy itself (preferred)
        const resolvedHatKeys = this.policy?.targets?.resolvedHatKeys || [];

        for (const hatKey of orgHats.hatsKeys) {
            const hat = orgHats[hatKey];
            if (hat && typeof hat === 'object' && 'name' in hat) {
                const hatObj = hat as OrgHat;

                // Check if this hat is in the policy's resolvedHatKeys (query model)
                // OR if this hat has the policy in its policyKeys (legacy model)
                const isInResolvedHatKeys = resolvedHatKeys.includes(hatKey);
                const hatPolicyKeys = hatObj.policyKeys || {};
                const isInHatPolicyKeys =
                    policyKey in hatPolicyKeys || Object.values(hatPolicyKeys).includes(policyKey);

                if (isInResolvedHatKeys || isInHatPolicyKeys) {
                    const userCount = hatObj.assignedToArray?.length || hatObj.userKeys?.length || 0;
                    this.hatsUsingPolicy.push({
                        key: hatKey,
                        name: hatObj.name || hatKey,
                        userCount,
                    });
                }
            }
        }

        // Sort by name
        this.hatsUsingPolicy.sort((a, b) => a.name.localeCompare(b.name));
        console.log(
            'loadHatsUsingPolicy > hatsUsingPolicy:',
            this.hatsUsingPolicy,
            'resolvedHatKeys:',
            resolvedHatKeys
        );
    }

    private loadUsersViaHats(org: Organization): void {
        const orgUsers = org?.orgUsers;

        if (!orgUsers) {
            this.usersViaHats = [];
            return;
        }

        // Map to track users and all hats they receive this policy through
        const usersMap = new Map<string, { key: string; name: string; hatNames: string[] }>();

        for (const hatInfo of this.hatsUsingPolicy) {
            const hat = org.orgHats?.[hatInfo.key] as OrgHat | undefined;
            if (hat) {
                const userKeys = hat.assignedToArray || hat.userKeys || [];

                for (const userKey of userKeys) {
                    const existingUser = usersMap.get(userKey);
                    if (existingUser) {
                        // Add another hat name to existing user
                        existingUser.hatNames.push(hatInfo.name);
                    } else {
                        const orgUser = orgUsers[userKey];
                        const userName =
                            orgUser && typeof orgUser === 'object'
                                ? (orgUser as { name?: string; userName?: string }).name ||
                                  (orgUser as { name?: string; userName?: string }).userName ||
                                  userKey
                                : userKey;

                        usersMap.set(userKey, {
                            key: userKey,
                            name: userName,
                            hatNames: [hatInfo.name],
                        });
                    }
                }
            }
        }

        this.usersViaHats = Array.from(usersMap.values()).map((u) => ({
            ...u,
            acknowledgmentStatus: this.policy?.requiresAcknowledgment ? 'pending' : 'not_required',
            acknowledgedAt: null,
        }));
        this.usersViaHats.sort((a, b) => a.name.localeCompare(b.name));

        // Load acknowledgment statuses for these users
        this.loadAcknowledgmentStatuses();

        // Ensure profile colors are loaded
        this.profileColorService.ensureLoadedMany(this.usersViaHats.map((u) => u.key));

        console.log('loadUsersViaHats > usersViaHats:', this.usersViaHats);
    }

    /**
     * Load acknowledgment statuses for users from policyAcknowledgments subcollection
     */
    private loadAcknowledgmentStatuses(): void {
        const orgKey = this.data.organization?.key;
        const policyKey = this.data.policyKey;

        if (!orgKey || !policyKey || this.usersViaHats.length === 0) return;

        // Query acknowledgments for this policy
        this.afs
            .collection(`organizations/${orgKey}/policyAcknowledgments`, (ref) =>
                ref.where('policyKey', '==', policyKey)
            )
            .get()
            .pipe(take(1))
            .subscribe({
                next: (snapshot) => {
                    const acknowledgments = new Map<string, PolicyAcknowledgment>();
                    snapshot.docs.forEach((doc) => {
                        const ack = doc.data() as PolicyAcknowledgment;
                        if (ack.uid) {
                            acknowledgments.set(ack.uid, ack);
                        }
                    });

                    // Update user statuses
                    for (const user of this.usersViaHats) {
                        const ack = acknowledgments.get(user.key);
                        if (ack && ack.isComplete) {
                            user.acknowledgmentStatus = 'acknowledged';
                            user.acknowledgedAt = this.parseFirestoreDate(ack.completedAt);
                        } else if (this.policy?.requiresAcknowledgment) {
                            user.acknowledgmentStatus = 'pending';
                        } else {
                            user.acknowledgmentStatus = 'not_required';
                        }
                    }

                    console.log('loadAcknowledgmentStatuses > updated users with ack status');
                },
                error: (err) => {
                    console.error('Error loading acknowledgment statuses:', err);
                },
            });
    }

    private loadPositionsWithPolicy(org: Organization): void {
        const orgParts = org?.orgParts || [];

        // Build a complete map of ALL parts including nested children
        const partNameMap = new Map<string, string>();
        const partTypeMap = new Map<string, string>();
        this.buildPartNameMap(orgParts, partNameMap, partTypeMap);

        console.log('loadPositionsWithPolicy > partNameMap size:', partNameMap.size);

        // Track positions to avoid duplicates
        const positionsMap = new Map<string, PositionWithPolicy>();

        for (const hatInfo of this.hatsUsingPolicy) {
            const hat = org.orgHats?.[hatInfo.key] as OrgHat | undefined;
            if (hat) {
                // Check positionArray
                const positionKeys = hat.positionArray || [];
                for (const posKey of positionKeys) {
                    if (!positionsMap.has(posKey)) {
                        positionsMap.set(posKey, {
                            key: posKey,
                            name: partNameMap.get(posKey) || posKey,
                            hatName: hatInfo.name,
                            partType: partTypeMap.get(posKey),
                        });
                    }
                }

                // Also check positions array (object array)
                const positions = hat.positions || [];
                for (const pos of positions) {
                    const posKey =
                        (pos as { key?: string; partKey?: string }).key ||
                        (pos as { key?: string; partKey?: string }).partKey;
                    if (posKey && !positionsMap.has(posKey)) {
                        const posName = (pos as { name?: string }).name || partNameMap.get(posKey) || posKey;
                        positionsMap.set(posKey, {
                            key: posKey,
                            name: posName,
                            hatName: hatInfo.name,
                            partType: partTypeMap.get(posKey),
                        });
                    }
                }

                // Also check orgPosition if it exists
                if (hat.orgPosition) {
                    const posKey = hat.orgPosition.key || hat.orgPosition.partKey;
                    if (posKey && !positionsMap.has(posKey)) {
                        positionsMap.set(posKey, {
                            key: posKey,
                            name: hat.orgPosition.name || hat.orgPosition.mainTitle || posKey,
                            hatName: hatInfo.name,
                            partType: partTypeMap.get(posKey),
                        });
                    }
                }
            }
        }

        this.positionsWithPolicy = Array.from(positionsMap.values());
        this.positionsWithPolicy.sort((a, b) => a.name.localeCompare(b.name));
        console.log('loadPositionsWithPolicy > positionsWithPolicy:', this.positionsWithPolicy);
    }

    private loadRevisions(): void {
        const revisions = this.policy?.revisions || [];

        // First, map the basic revision data including full snapshot
        const mappedRevisions: RevisionDisplay[] = revisions.map((rev) => ({
            revisionNumber: 0, // Will be set after sorting
            version: rev.version,
            date: this.parseFirestoreDate(rev.date),
            name: rev.name,
            writtenBy: rev.writtenBy,
            policyWriting: rev.policyWriting,
            changeNotes: rev.changeNotes,
            changes: [],
            isInitial: false,
            // Full snapshot data
            description: rev.description,
            category: rev.category,
            customCategory: rev.customCategory,
            status: rev.status,
            effectiveDate: this.parseFirestoreDate(rev.effectiveDate),
            publishDate: this.parseFirestoreDate(rev.publishDate),
            expiryDate: this.parseFirestoreDate(rev.expiryDate),
            acknowledgmentDeadline: this.parseFirestoreDate(rev.acknowledgmentDeadline),
            targets: rev.targets,
            approvalWorkflow: rev.approvalWorkflow,
            requiresAcknowledgment: rev.requiresAcknowledgment,
            isMandatory: rev.isMandatory,
        }));

        // Sort by date ascending (oldest first) for chronological revision numbering
        mappedRevisions.sort((a, b) => {
            const dateA = a.date instanceof Date ? a.date.getTime() : 0;
            const dateB = b.date instanceof Date ? b.date.getTime() : 0;
            return dateA - dateB;
        });

        // Assign revision numbers and detect changes
        for (let i = 0; i < mappedRevisions.length; i++) {
            const current = mappedRevisions[i];
            const previous = i > 0 ? mappedRevisions[i - 1] : null;

            // Revision number is 1-based
            current.revisionNumber = i + 1;

            if (i === 0) {
                // First/earliest revision - show initial values as "changes"
                current.isInitial = true;
                const initialChanges: RevisionChange[] = [
                    { field: 'initial', label: 'Initial Version', icon: 'add_circle' },
                ];

                // Show what was initially set
                if (current.name) {
                    initialChanges.push({
                        field: 'name',
                        label: 'Name',
                        icon: 'title',
                        fromValue: '(new)',
                        toValue: current.name,
                    });
                }
                if (current.description) {
                    initialChanges.push({
                        field: 'description',
                        label: 'Description',
                        icon: 'notes',
                        fromValue: '(none)',
                        toValue: this.truncateText(current.description, 50),
                    });
                }
                if (current.category) {
                    initialChanges.push({
                        field: 'category',
                        label: 'Category',
                        icon: 'category',
                        fromValue: '(none)',
                        toValue: current.category,
                    });
                }
                if (current.targets) {
                    const targetsDisplay = this.formatTargets(current.targets);
                    if (targetsDisplay) {
                        initialChanges.push({
                            field: 'targets',
                            label: 'Target Audience',
                            icon: 'groups',
                            fromValue: '(none)',
                            toValue: targetsDisplay,
                        });
                    }
                }
                if (current.requiresAcknowledgment) {
                    initialChanges.push({
                        field: 'requiresAck',
                        label: 'Requires Acknowledgment',
                        icon: 'fact_check',
                        fromValue: 'No',
                        toValue: 'Yes',
                    });
                }
                if (current.approvalWorkflow) {
                    const approversDisplay = this.formatApprovers(current.approvalWorkflow);
                    if (approversDisplay) {
                        initialChanges.push({
                            field: 'approvers',
                            label: 'Approvers',
                            icon: 'how_to_reg',
                            fromValue: '(none)',
                            toValue: approversDisplay,
                        });
                    }
                }

                current.changes = initialChanges;
            } else if (previous) {
                const changes: RevisionChange[] = [];

                // Check for name change
                if (current.name !== previous.name) {
                    changes.push({
                        field: 'name',
                        label: 'Name',
                        icon: 'title',
                        fromValue: previous.name,
                        toValue: current.name,
                    });
                }

                // Check for description change
                if (current.description !== previous.description) {
                    changes.push({
                        field: 'description',
                        label: 'Description',
                        icon: 'notes',
                        fromValue: this.truncateText(previous.description || '(none)', 50),
                        toValue: this.truncateText(current.description || '(none)', 50),
                    });
                }

                // Check for content change
                if (current.policyWriting !== previous.policyWriting) {
                    changes.push({
                        field: 'content',
                        label: 'Content',
                        icon: 'description',
                        fromValue: this.truncateText(previous.policyWriting || '', 50),
                        toValue: this.truncateText(current.policyWriting || '', 50),
                    });
                }

                // Check for category change
                if (current.category !== previous.category) {
                    changes.push({
                        field: 'category',
                        label: 'Category',
                        icon: 'category',
                        fromValue: previous.category || '(none)',
                        toValue: current.category || '(none)',
                    });
                }

                // Check for status change
                if (current.status !== previous.status) {
                    changes.push({
                        field: 'status',
                        label: 'Status',
                        icon: 'flag',
                        fromValue: previous.status || '(none)',
                        toValue: current.status || '(none)',
                    });
                }

                // Check for effective date change
                if (this.formatDateShort(current.effectiveDate) !== this.formatDateShort(previous.effectiveDate)) {
                    changes.push({
                        field: 'effectiveDate',
                        label: 'Effective Date',
                        icon: 'event',
                        fromValue: this.formatDateShort(previous.effectiveDate) || '(none)',
                        toValue: this.formatDateShort(current.effectiveDate) || '(none)',
                    });
                }

                // Check for publish date change
                if (this.formatDateShort(current.publishDate) !== this.formatDateShort(previous.publishDate)) {
                    changes.push({
                        field: 'publishDate',
                        label: 'Publish Date',
                        icon: 'publish',
                        fromValue: this.formatDateShort(previous.publishDate) || '(none)',
                        toValue: this.formatDateShort(current.publishDate) || '(none)',
                    });
                }

                // Check for expiry date change
                if (this.formatDateShort(current.expiryDate) !== this.formatDateShort(previous.expiryDate)) {
                    changes.push({
                        field: 'expiryDate',
                        label: 'Expiry Date',
                        icon: 'event_busy',
                        fromValue: this.formatDateShort(previous.expiryDate) || '(none)',
                        toValue: this.formatDateShort(current.expiryDate) || '(none)',
                    });
                }

                // Check for acknowledgment deadline change
                if (
                    this.formatDateShort(current.acknowledgmentDeadline) !==
                    this.formatDateShort(previous.acknowledgmentDeadline)
                ) {
                    changes.push({
                        field: 'deadline',
                        label: 'Acknowledgment Deadline',
                        icon: 'timer',
                        fromValue: this.formatDateShort(previous.acknowledgmentDeadline) || '(none)',
                        toValue: this.formatDateShort(current.acknowledgmentDeadline) || '(none)',
                    });
                }

                // Infer target mode from snapshot data
                // If there are parts/users/hats in the snapshot, mode was 'selected' even if not explicitly set
                const inferMode = (targets: PolicyTargetsSnapshot | undefined): string => {
                    if (!targets) return 'all_organization';
                    if (targets.mode) return targets.mode;
                    // Infer from counts - if there are any selections, it was 'selected' mode
                    if (
                        (targets.partCount && targets.partCount > 0) ||
                        (targets.directUserCount && targets.directUserCount > 0) ||
                        (targets.directHatCount && targets.directHatCount > 0)
                    ) {
                        return 'selected';
                    }
                    return 'all_organization';
                };

                const prevMode = inferMode(previous.targets);
                const currMode = inferMode(current.targets);

                // Check for target mode change (All Organization vs Selected Targets)
                if (prevMode !== currMode) {
                    changes.push({
                        field: 'targetMode',
                        label: 'Target Audience',
                        icon: 'groups',
                        fromValue: prevMode === 'all_organization' ? 'All Organization' : 'Selected Targets',
                        toValue: currMode === 'all_organization' ? 'All Organization' : 'Selected Targets',
                    });
                }

                // Check for targets selection change (within selected mode or any change in counts)
                const prevTargets = this.formatTargets(previous.targets);
                const currTargets = this.formatTargets(current.targets);
                // Show targets change if the formatted display is different and mode didn't change
                if (prevTargets !== currTargets && prevMode === currMode) {
                    changes.push({
                        field: 'targets',
                        label: 'Target Selection',
                        icon: 'checklist',
                        fromValue: prevTargets || '(none)',
                        toValue: currTargets || '(none)',
                    });
                }

                // Check for approvers change
                const prevApprovers = this.formatApprovers(previous.approvalWorkflow);
                const currApprovers = this.formatApprovers(current.approvalWorkflow);
                if (prevApprovers !== currApprovers) {
                    changes.push({
                        field: 'approvers',
                        label: 'Approvers',
                        icon: 'how_to_reg',
                        fromValue: prevApprovers || '(none)',
                        toValue: currApprovers || '(none)',
                    });
                }

                // Check for requiresAcknowledgment change
                if (current.requiresAcknowledgment !== previous.requiresAcknowledgment) {
                    changes.push({
                        field: 'requiresAck',
                        label: 'Requires Acknowledgment',
                        icon: 'fact_check',
                        fromValue: previous.requiresAcknowledgment ? 'Yes' : 'No',
                        toValue: current.requiresAcknowledgment ? 'Yes' : 'No',
                    });
                }

                // Check for isMandatory change
                if (current.isMandatory !== previous.isMandatory) {
                    changes.push({
                        field: 'mandatory',
                        label: 'Mandatory',
                        icon: 'priority_high',
                        fromValue: previous.isMandatory ? 'Yes' : 'No',
                        toValue: current.isMandatory ? 'Yes' : 'No',
                    });
                }

                // Check if this is a "Restored" revision (marked by writtenBy containing 'restored')
                if (current.writtenBy && current.writtenBy.toLowerCase().includes('restored')) {
                    // Add a special "Restored" indicator at the beginning
                    changes.unshift({
                        field: 'restored',
                        label: 'Restored',
                        icon: 'restore',
                        fromValue: '',
                        toValue: current.changeNotes || 'Restored from previous version',
                    });
                }

                // Check if this is a "Pre-restore backup" revision
                if (current.writtenBy && current.writtenBy.toLowerCase().includes('pre-restore')) {
                    changes.unshift({
                        field: 'backup',
                        label: 'Pre-Restore Backup',
                        icon: 'backup',
                        fromValue: '',
                        toValue: current.changeNotes || 'Saved before restore',
                    });
                }

                // If no specific changes detected, mark as updated
                if (changes.length === 0) {
                    changes.push({ field: 'updated', label: 'Updated', icon: 'edit' });
                }

                current.changes = changes;
            }
        }

        // Sort by revision number descending (newest first) for display
        this.revisions = mappedRevisions.sort((a, b) => b.revisionNumber - a.revisionNumber);
        console.log('loadRevisions > revisions:', this.revisions);
    }

    /**
     * Format a date for short display (without time)
     */
    private formatDateShort(date: Date | null | undefined): string {
        if (!date || !(date instanceof Date)) return '';
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    }

    /**
     * Format targets for display
     */
    public formatTargets(targets?: PolicyTargetsSnapshot): string {
        if (!targets) return '';

        // If mode is all_organization, always show that regardless of counts
        if (targets.mode === 'all_organization') {
            return 'All Organization';
        }

        const parts: string[] = [];
        if (targets.partCount && targets.partCount > 0) {
            parts.push(`${targets.partCount} part${targets.partCount > 1 ? 's' : ''}`);
        }
        if (targets.directHatCount && targets.directHatCount > 0) {
            parts.push(`${targets.directHatCount} hat${targets.directHatCount > 1 ? 's' : ''}`);
        }
        if (targets.directUserCount && targets.directUserCount > 0) {
            parts.push(`${targets.directUserCount} user${targets.directUserCount > 1 ? 's' : ''}`);
        }

        // If mode is 'selected' but no specific targets, show that
        if (parts.length === 0) {
            return targets.mode === 'selected' ? 'Selected (none specified)' : targets.mode || '';
        }

        return parts.join(', ');
    }

    /**
     * Format approvers for display
     */
    public formatApprovers(workflow?: PolicyApprovalWorkflowSnapshot): string {
        if (!workflow || !workflow.approverNames || workflow.approverNames.length === 0) return '';
        return workflow.approverNames.join(' → ');
    }

    /**
     * Parse Firestore timestamps, dates, or timestamp-like objects into Date objects.
     * Handles:
     * - Firestore Timestamp with .toDate() method
     * - Plain objects with _seconds/_nanoseconds (JSON serialized Timestamp)
     * - Plain objects with seconds/nanoseconds
     * - Date objects
     * - ISO date strings
     */
    private parseFirestoreDate(date: any): Date | null {
        if (!date) return null;

        // Already a Date object
        if (date instanceof Date) {
            return isNaN(date.getTime()) ? null : date;
        }

        // Firestore Timestamp with .toDate() method
        if (typeof date.toDate === 'function') {
            try {
                return date.toDate();
            } catch (e) {
                console.warn('Error calling toDate():', e);
            }
        }

        // Plain object with _seconds (JSON serialized Firestore Timestamp)
        if (date._seconds !== undefined) {
            return new Date(date._seconds * 1000);
        }

        // Plain object with seconds (another common format)
        if (date.seconds !== undefined) {
            return new Date(date.seconds * 1000);
        }

        // ISO string or other string format
        if (typeof date === 'string') {
            const parsed = new Date(date);
            return isNaN(parsed.getTime()) ? null : parsed;
        }

        // Number (milliseconds since epoch)
        if (typeof date === 'number') {
            return new Date(date);
        }

        return null;
    }

    /**
     * Recursively builds a map of all org part keys to their names,
     * including nested children at all levels.
     */
    private buildPartNameMap(parts: any[], partNameMap: Map<string, string>, partTypeMap?: Map<string, string>): void {
        for (const part of parts) {
            // Add this part to the map using both key and partKey
            const partKey = part.partKey || part.key;
            const partName = part.name || part.mainTitle || partKey;
            const pType = part.partType?.toLowerCase() || '';

            if (partKey) {
                partNameMap.set(partKey, partName);
                if (partTypeMap && pType) partTypeMap.set(partKey, pType);
            }
            if (part.key && part.key !== partKey) {
                partNameMap.set(part.key, partName);
                if (partTypeMap && pType) partTypeMap.set(part.key, pType);
            }

            // Recursively process children
            if (part.children && Array.isArray(part.children)) {
                this.buildPartNameMap(part.children, partNameMap, partTypeMap);
            }
        }
    }

    public getTotalUsersCount(): number {
        return this.hatsUsingPolicy.reduce((total, hat) => total + hat.userCount, 0);
    }

    /**
     * Gets the avatar URL for a user.
     * Returns photoThumbnail if available, otherwise falls back to generated avatar.
     */
    public getUserAvatarUrl(user: UserViaHat): string {
        const org = this.data.organization;

        // Check organization's orgUsers for photoThumbnail
        if (org?.orgUsers?.[user.key]?.photoThumbnail) {
            return org.orgUsers[user.key].photoThumbnail!;
        }

        // Fall back to generated avatar
        const name = user.name || 'User';
        return this.profileColorService.getAvatarUrl(user.key, name, 40);
    }

    public formatDate(date: Date | string | null | undefined): string {
        if (!date) return 'N/A';

        // If it's already a Date, use it directly
        let d: Date;
        if (date instanceof Date) {
            d = date;
        } else if (typeof date === 'string') {
            d = new Date(date);
        } else {
            // Try to parse using our helper for any other format
            const parsed = this.parseFirestoreDate(date);
            if (!parsed) return 'N/A';
            d = parsed;
        }

        // Check for invalid date
        if (isNaN(d.getTime())) return 'N/A';

        return d.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    }

    public getStatusConfig(status: PolicyStatus | undefined | null) {
        return this.statusConfig[status || 'draft'] || this.statusConfig['draft'];
    }

    public getCategoryIcon(category: PolicyCategory | undefined | null): string {
        const cat = this.categories.find((c) => c.value === category);
        return cat?.icon || 'description';
    }

    public getCategoryLabel(category: PolicyCategory | undefined | null): string {
        const cat = this.categories.find((c) => c.value === category);
        return cat?.label || 'General';
    }

    public formatTimestamp(timestamp: any): string {
        if (!timestamp) return '-';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    }

    public getTargetsSummary(): { mode: string; partCount: number; hatCount: number; userCount: number } {
        const targets = this.policy?.targets;
        if (!targets) {
            return { mode: 'none', partCount: 0, hatCount: 0, userCount: 0 };
        }
        return {
            mode: targets.mode || 'none',
            partCount: targets.parts?.length || 0,
            hatCount: targets.directHats?.length || 0,
            userCount: targets.directUsers?.length || 0,
        };
    }

    // === Approval Workflow Helpers ===

    /**
     * Get approval workflow steps
     */
    public getApprovalSteps(): PolicyApprovalStep[] {
        return this.policy?.approvalWorkflow?.steps || [];
    }

    /**
     * Check if policy has approval workflow configured
     */
    public hasApprovalWorkflow(): boolean {
        return (this.policy?.approvalWorkflow?.steps?.length || 0) > 0;
    }

    /**
     * Get approval step status config
     */
    public getApprovalStepStatusConfig(status: string | undefined) {
        return this.approvalStatusConfig[status || 'pending'] || this.approvalStatusConfig['pending'];
    }

    /**
     * Get workflow overall status
     */
    public getWorkflowStatus(): string {
        return this.policy?.approvalWorkflow?.workflowStatus || 'not_started';
    }

    /**
     * Get count of completed approval steps
     */
    public getApprovedStepsCount(): number {
        return this.getApprovalSteps().filter((s) => s.status === 'approved').length;
    }

    // === Targeted Parts/Hats/Users Helpers ===

    /**
     * Get targeted parts list
     */
    public getTargetedParts(): PolicyPartTarget[] {
        return this.policy?.targets?.parts || [];
    }

    /**
     * Get targeted hats list
     */
    public getTargetedHats(): PolicyHatTarget[] {
        return this.policy?.targets?.directHats || [];
    }

    /**
     * Get targeted users list
     */
    public getTargetedUsers(): PolicyUserTarget[] {
        return this.policy?.targets?.directUsers || [];
    }

    /**
     * Check if policy has any configured targets
     */
    public hasConfiguredTargets(): boolean {
        const targets = this.policy?.targets;
        if (!targets) return false;
        if (targets.mode === 'all_organization') return true;
        return (
            (targets.parts?.length || 0) > 0 ||
            (targets.directHats?.length || 0) > 0 ||
            (targets.directUsers?.length || 0) > 0
        );
    }

    // === Approval Actions ===

    /**
     * Check if policy can be approved directly (no workflow or workflow not started)
     * TODO: Add permission check when permissions are configured
     */
    public canApproveDirectly(): boolean {
        if (!this.policy) return false;
        // Can approve if status is draft and no workflow configured
        return (
            this.policy.status === 'draft' &&
            (!this.policy.approvalWorkflow || this.policy.approvalWorkflow.steps.length === 0)
        );
    }

    /**
     * Approve the policy directly (for policies without approval workflow).
     *
     * Goes through `api-policyApproval`'s `submit` — with no workflow
     * configured the server approves outright (design §2a), so the outcome is
     * the same as the old direct write but the decision, the blame and the
     * audit entry are all made server-side (OE-75).
     */
    public async approvePolicy(): Promise<void> {
        if (!this.policy) return;

        const confirmed = await this.dialogService.asyncConfirm(
            'Approve Policy?',
            `Are you sure you want to approve "${this.policy.name}"?\n\nThis will change the status to Approved.`
        );
        if (!confirmed) return;

        const result = await this.runApprovalCall((target) => this.approvalService.submit(target));
        if (!result?.ok) return;

        this.snackbar.open(`Policy "${this.policy?.name}" approved successfully`, 'Close', { duration: 3000 });
        // Close dialog with result indicating policy was approved
        this.dialogRef.close({ approved: true, policyKey: this.policy?.key });
    }

    // === Per-step approval chain actions (OE-75, design §2e) ===

    /**
     * May the signed-in user decide this step?
     *
     * Only the person the chain is actually waiting on — the approver frozen
     * onto the CURRENT step (§2c) while that step is still pending. The server
     * enforces the same rule (`ERR_NOT_CURRENT_APPROVER`); this keeps the
     * buttons off screens where the click could only be refused.
     */
    public canDecideStep(step: PolicyApprovalStep | undefined, index: number): boolean {
        const workflow = this.policy?.approvalWorkflow;
        if (!step || !workflow || this.policy?.status !== 'pending_approval') return false;
        if (workflow.workflowStatus !== 'in_progress') return false;
        if (workflow.currentStepIndex !== index || step.status !== 'pending') return false;
        return !!this.currentUid && step.approver?.uid === this.currentUid;
    }

    /** The step the chain is currently waiting on (for template highlighting). */
    public isCurrentStep(index: number): boolean {
        return this.policy?.status === 'pending_approval' && this.policy?.approvalWorkflow?.currentStepIndex === index;
    }

    /**
     * May the signed-in user bypass the remaining steps?
     *
     * Org owners only, and only while a chain is actually running. The server
     * additionally requires `manage-org-policies` (or a global admin claim),
     * which the browser cannot see — a refusal is surfaced as a message rather
     * than guessed at here.
     */
    public canOverrideApproval(): boolean {
        if (!this.policy || this.policy.status !== 'pending_approval') return false;
        if (!this.hasApprovalWorkflow()) return false;
        const owners = (this.data.organization?.owners || []) as string[];
        return !!this.currentUid && owners.includes(this.currentUid);
    }

    /** Approve the current step. Comments are optional on an approval. */
    public async approveStep(step: PolicyApprovalStep, index: number): Promise<void> {
        if (!this.canDecideStep(step, index)) return;

        const confirmed = await this.dialogService.asyncConfirm(
            `Approve "${step.name}"?`,
            `This approves your step of "${this.policy?.name}".\n\n` +
                `The next approver is notified automatically; if this is the last step the policy becomes Approved.`
        );
        if (!confirmed) return;

        const result = await this.runApprovalCall((target) =>
            this.approvalService.decide({ ...target, decision: 'approved' })
        );
        if (!result?.ok) return;

        this.snackbar.open(result.message || 'Step approved', 'Close', { duration: 4000 });
    }

    /** Reject the current step. The server requires a reason. */
    public async rejectStep(step: PolicyApprovalStep, index: number): Promise<void> {
        if (!this.canDecideStep(step, index)) return;

        const prompt = await this.dialogService.asyncPrompt({
            title: `Reject "${step.name}"?`,
            description:
                `This returns "${this.policy?.name}" to draft so the author can revise and resubmit.\n\n` +
                `Please say why:`,
            inputLabel: 'Reason for rejection',
            inputPlaceholder: 'e.g., Conflicts with the current leave policy',
            inputRequired: true,
            confirmButtonText: 'Reject',
            confirmButtonColor: 'warn',
        });

        const comments = prompt?.value?.trim();
        if (!prompt?.confirmed || !comments) return;

        const result = await this.runApprovalCall((target) =>
            this.approvalService.decide({ ...target, decision: 'rejected', comments })
        );
        if (!result?.ok) return;

        this.snackbar.open(result.message || 'Policy rejected and returned to draft', 'Close', { duration: 4000 });
    }

    /** Owner/admin bypass of the outstanding steps. Reason is mandatory. */
    public async overrideApproval(): Promise<void> {
        if (!this.canOverrideApproval() || !this.policy) return;

        const prompt = await this.dialogService.asyncPrompt({
            title: `Override the approval chain?`,
            description:
                `This approves "${this.policy.name}" immediately and marks every outstanding step as skipped.\n\n` +
                `The reason is recorded against the policy:`,
            inputLabel: 'Reason for the override',
            inputPlaceholder: 'e.g., Board resolution 2026-14 mandates immediate adoption',
            inputRequired: true,
            confirmButtonText: 'Override',
            confirmButtonColor: 'warn',
        });

        const reason = prompt?.value?.trim();
        if (!prompt?.confirmed || !reason) return;

        const result = await this.runApprovalCall((target) => this.approvalService.override({ ...target, reason }));
        if (!result?.ok) return;

        this.snackbar.open(result.message || 'Approval chain overridden', 'Close', { duration: 4000 });
    }

    /**
     * Shared shell for the four approval calls: guards double-clicks, and turns
     * a refusal into a message instead of a silent no-op. Returns undefined when
     * the call could not be made at all.
     */
    private async runApprovalCall(
        call: (target: { orgKey: string; policyKey: string; apiBaseUrl?: string }) => Promise<{
            ok: boolean;
            message: string;
            error?: string;
        }>
    ): Promise<{ ok: boolean; message: string; error?: string } | undefined> {
        if (!this.policy || this.approvalInFlight) return undefined;

        this.approvalInFlight = true;
        try {
            const result = await call({
                orgKey: this.data.organization.key,
                policyKey: this.policy.key,
                apiBaseUrl: this.orgDialogService?.apiBaseUrl,
            });

            if (!result.ok) {
                console.error('Policy approval refused:', result.error, result.message);
                this.snackbar.open(result.message, 'Close', { duration: 5000 });
            }
            return result;
        } finally {
            this.approvalInFlight = false;
        }
    }

    // === Audit Trail Helpers ===

    /**
     * Get CSS class for audit entry icon based on action type
     */
    public getAuditEntryClass(action: string): string {
        switch (action) {
            case 'created':
                return 'action-created';
            case 'approved':
            case 'direct_approval':
            case 'workflow_step_approved':
                return 'action-approved';
            case 'admin_override_approval':
                return 'action-admin-override';
            case 'rejected':
            case 'workflow_step_rejected':
                return 'action-rejected';
            case 'published':
                return 'action-published';
            default:
                return 'action-default';
        }
    }

    /**
     * Get icon for audit entry based on action type
     */
    public getAuditEntryIcon(action: string): string {
        switch (action) {
            case 'created':
                return 'add_circle';
            case 'updated':
                return 'edit';
            case 'submitted_for_approval':
                return 'send';
            case 'approved':
            case 'direct_approval':
            case 'workflow_step_approved':
                return 'check_circle';
            case 'admin_override_approval':
                return 'admin_panel_settings';
            case 'rejected':
            case 'workflow_step_rejected':
                return 'cancel';
            case 'published':
                return 'publish';
            case 'revoked':
                return 'remove_circle';
            case 'cancelled':
                return 'block';
            case 'deleted':
                return 'delete';
            case 'reinstated':
                return 'restore';
            default:
                return 'history';
        }
    }

    /**
     * Format audit action for display
     */
    public formatAuditAction(action: string): string {
        const actionLabels: Record<string, string> = {
            created: 'Created',
            updated: 'Updated',
            submitted_for_approval: 'Submitted for Approval',
            approved: 'Approved',
            direct_approval: 'Direct Approval',
            admin_override_approval: 'Admin Override Approval',
            rejected: 'Rejected',
            published: 'Published',
            revoked: 'Revoked',
            cancelled: 'Cancelled',
            deleted: 'Deleted',
            reinstated: 'Reinstated',
            workflow_step_approved: 'Workflow Step Approved',
            workflow_step_rejected: 'Workflow Step Rejected',
            unauthorized_publish_blocked: 'Publish Blocked (Not Authorized)',
        };
        return actionLabels[action] || action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    }

    // === Revision Management ===

    /**
     * Selected revision for preview
     */
    public selectedRevision: RevisionDisplay | null = null;

    /**
     * Preview a revision's content
     */
    public previewRevision(revision: RevisionDisplay): void {
        this.selectedRevision = revision;
    }

    /**
     * Close revision preview
     */
    public closeRevisionPreview(): void {
        this.selectedRevision = null;
    }

    /**
     * Check if the current policy status allows restoring to a revision.
     * Restore is only allowed for editable statuses (draft, pending_approval)
     * For published/approved policies, user should create a new version instead.
     */
    public canRestoreRevision(): boolean {
        const status = this.policy?.status;
        return status === 'draft' || status === 'pending_approval';
    }

    /**
     * Check if a revision is the latest (most recent) one.
     * The latest revision represents the current state, so restoring to it is pointless.
     */
    public isLatestRevision(revision: RevisionDisplay): boolean {
        if (!this.revisions || this.revisions.length === 0) return false;
        // Revisions are sorted by date ascending, so the last one is the latest
        const latestRevision = this.revisions[this.revisions.length - 1];
        return revision.revisionNumber === latestRevision.revisionNumber;
    }

    /**
     * Get message explaining why restore is not available
     */
    public getRestoreNotAvailableReason(): string {
        const status = this.policy?.status;
        switch (status) {
            case 'published':
                return 'Published policies cannot be modified. Create a new version instead.';
            case 'approved':
            case 'scheduled':
                return 'Approved policies cannot be modified. Either publish or cancel first.';
            case 'expired':
            case 'archived':
            case 'cancelled':
            case 'revoked':
                return 'This policy is no longer active. Create a new version to make changes.';
            default:
                return 'Restore is not available for this policy status.';
        }
    }

    /**
     * Restore policy content from a specific revision.
     * This will:
     * 1. Save current state as a new revision (safety snapshot)
     * 2. Update policy content to match the selected revision
     * 3. Keep policy in its current status
     */
    public async restoreToRevision(revision: RevisionDisplay): Promise<void> {
        if (!this.policy || !this.canRestoreRevision()) {
            this.snackbar.open(this.getRestoreNotAvailableReason(), 'Close', { duration: 4000 });
            return;
        }

        // Check if this is the latest revision (no point restoring to current state)
        if (this.isLatestRevision(revision)) {
            this.snackbar.open('This is the current revision - nothing to restore', 'Close', { duration: 3000 });
            return;
        }

        const confirmed = await this.dialogService.asyncConfirm(
            `Restore to Revision ${revision.version}?`,
            `This will:\n` +
                `• Replace ALL policy fields with Revision ${revision.version} values\n` +
                `• Create a new revision entry recording the restore\n` +
                `• Keep the policy in "${this.policy.status}" status\n\n` +
                `The current state is already saved in the previous revision.`
        );
        if (!confirmed) return;

        // Get current user for writtenBy field
        const currentUser = await this.auth.currentUser;
        const currentUserName = currentUser?.displayName || currentUser?.email || 'Unknown User';

        const now = Timestamp.now();
        const currentPolicy = this.policy;

        // Get existing revisions
        const existingRevisions = [...(this.policy.revisions || [])];

        // Get next version number
        const maxVersion = Math.max(0, ...existingRevisions.map((r) => r.version));

        // Create a single "Restored" revision entry
        // This captures the NEW state (what we're restoring to) - just like a normal save
        // The PREVIOUS state is already in the last revision, so no separate backup needed
        const restoredRevision: any = {
            version: maxVersion + 1,
            date: now,
            name: revision.name,
            policyWriting: revision.policyWriting,
            writtenBy: `${currentUserName} (restored)`,
            changeNotes: `Restored to Revision ${revision.version}`,
            // Copy all snapshot fields from the revision being restored to
            description: revision.description,
            category: revision.category,
            customCategory: revision.customCategory,
            status: revision.status,
            effectiveDate: revision.effectiveDate,
            publishDate: revision.publishDate,
            expiryDate: revision.expiryDate,
            acknowledgmentDeadline: revision.acknowledgmentDeadline,
            targets: revision.targets,
            approvalWorkflow: revision.approvalWorkflow,
            requiresAcknowledgment: revision.requiresAcknowledgment,
            isMandatory: revision.isMandatory,
        };

        // Remove undefined fields from restored revision
        Object.keys(restoredRevision).forEach((key) => {
            if (restoredRevision[key] === undefined) {
                delete restoredRevision[key];
            }
        });

        const updatedRevisions = [...existingRevisions, restoredRevision];

        // Prepare the update with ALL restorable fields from the revision
        const updateData: any = {
            // Basic content
            name: revision.name,
            policyWriting: revision.policyWriting || currentPolicy.policyWriting,
            description: revision.description !== undefined ? revision.description : null,
            category: revision.category || currentPolicy.category,
            customCategory: revision.customCategory !== undefined ? revision.customCategory : null,
            // Note: We keep the current status, not restore to old status
            // status: revision.status || currentPolicy.status,

            // Dates - restore if present in revision, otherwise clear
            effectiveDate: revision.effectiveDate || null,
            publishDate: revision.publishDate || null,
            expiryDate: revision.expiryDate || null,
            acknowledgmentDeadline: revision.acknowledgmentDeadline || null,

            // Settings
            requiresAcknowledgment: revision.requiresAcknowledgment ?? false,
            isMandatory: revision.isMandatory ?? false,

            // Revisions and audit
            revisions: updatedRevisions,
            modifiedOn: now,
        };
        stampBlame(updateData, currentUser?.uid);
        stampLastChangedBy(updateData, currentUser);

        // Restore targets - use full target data if available (new revisions),
        // or fall back to mode-only for legacy revisions
        if (revision.targets) {
            // Check if we have full target data (new format) or just names (legacy)
            const hasFullData =
                revision.targets.parts?.length ||
                revision.targets.directUsers?.length ||
                revision.targets.directHats?.length;

            if (hasFullData) {
                // Full restore from complete snapshot
                updateData.targets = {
                    mode: revision.targets.mode || 'all_organization',
                    parts: revision.targets.parts || [],
                    directUsers: revision.targets.directUsers || [],
                    directHats: revision.targets.directHats || [],
                };
            } else {
                // Legacy revision - only mode available, parts need re-selection
                updateData.targets = {
                    mode: revision.targets.mode || 'all_organization',
                    parts: [],
                    directUsers: [],
                    directHats: [],
                };
            }
        } else {
            // No snapshot - default to all_organization (most likely for old revisions)
            updateData.targets = {
                mode: 'all_organization',
                parts: [],
                directUsers: [],
                directHats: [],
            };
        }

        // Restore approval workflow - clear if snapshot exists or didn't have one
        // Cannot fully restore workflow from snapshot (only names stored)
        updateData.approvalWorkflow = null;

        // Update the policy document
        this.afs
            .collection('organizations')
            .doc(this.data.organization.key)
            .collection('policies')
            .doc(this.policy.key)
            .update(updateData)
            .then(() => {
                // Check if this was a full restore or partial (legacy revision without full target data)
                const hasFullTargetData =
                    revision.targets?.parts?.length ||
                    revision.targets?.directUsers?.length ||
                    revision.targets?.directHats?.length;
                const hadTargets =
                    revision.targets?.partCount ||
                    revision.targets?.directHatCount ||
                    revision.targets?.directUserCount;

                let message = `Restored to Revision ${revision.version}. New revision ${restoredRevision.version} created.`;

                // Only warn about re-selection if there were targets but no full data (legacy revision)
                if (hadTargets && !hasFullTargetData) {
                    message += ' Note: Legacy revision - target selections need to be re-selected.';
                }

                this.snackbar.open(message, 'Close', { duration: 5000 });
                // Close preview
                this.selectedRevision = null;
                // Reload revisions
                this.loadRevisions();
            })
            .catch((error) => {
                console.error('Error restoring revision:', error);
                this.snackbar.open('Error restoring revision', 'Close', { duration: 3000 });
            });
    }

    /**
     * Strip HTML tags for plain text preview
     */
    public stripHtml(html: string): string {
        if (!html) return '';
        // Create a temporary div to parse HTML
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    }

    /**
     * Truncate text for preview
     */
    public truncateText(text: string, maxLength = 200): string {
        if (!text) return '';
        const stripped = this.stripHtml(text);
        if (stripped.length <= maxLength) return stripped;
        return stripped.substring(0, maxLength) + '...';
    }

    /**
     * Converts plain text or markdown-style content to HTML for display.
     * If content is already HTML, returns it as-is.
     */
    private convertToHtml(content: string): string {
        if (!content) return '';

        // Check if already HTML (has common HTML tags)
        if (
            content.includes('<p>') ||
            content.includes('<h1>') ||
            content.includes('<h2>') ||
            content.includes('<div>') ||
            content.includes('<ul>') ||
            content.includes('<ol>')
        ) {
            return content;
        }

        let html = content;

        // Convert headers (## Header -> <h2>Header</h2>)
        html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
        html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
        html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');

        // Convert bold (**text** or __text__)
        html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
        html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

        // Convert italic (*text* or _text_) - be careful not to match already converted bold
        html = html.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>');
        html = html.replace(/(?<!_)_([^_]+)_(?!_)/g, '<em>$1</em>');

        // Convert unordered lists (- item or * item at start of line)
        const lines = html.split('\n');
        let inList = false;
        const processedLines: string[] = [];

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const listMatch = line.match(/^[\s]*[-*]\s+(.+)$/);

            if (listMatch) {
                if (!inList) {
                    processedLines.push('<ul>');
                    inList = true;
                }
                processedLines.push(`<li>${listMatch[1]}</li>`);
            } else {
                if (inList) {
                    processedLines.push('</ul>');
                    inList = false;
                }
                // Wrap non-empty, non-tag lines in paragraphs
                const trimmedLine = line.trim();
                if (
                    trimmedLine &&
                    !trimmedLine.startsWith('<h') &&
                    !trimmedLine.startsWith('<ul') &&
                    !trimmedLine.startsWith('<li') &&
                    !trimmedLine.startsWith('</')
                ) {
                    processedLines.push(`<p>${line}</p>`);
                } else if (trimmedLine) {
                    processedLines.push(line);
                }
            }
        }

        if (inList) {
            processedLines.push('</ul>');
        }

        return processedLines.join('\n');
    }
}
